import { useState } from "react";
import { router } from "@inertiajs/react";
import Modal from "@/components/ui/Modal";
import Button from "@/components/ui/Button";
import type { ProgramConfig } from "@/interfaces/program";

// A three-card welcome shown once, the first time someone signs in.
//
// It's pitched at organizers on purpose: attendees frequently have no Hack Club
// account at all, so almost everyone who reaches a signed-in dashboard is here
// to run an event or is considering it. The flow explains what being the point
// of contact means and ends on "apply".
//
// Any exit marks onboarding complete -- skipping, closing, or finishing. A
// welcome that reappears on every page load is worse than one someone dismissed
// early by accident.
export default function WelcomeTour({
  program,
  name,
}: {
  program: ProgramConfig;
  name: string;
}) {
  const [open, setOpen] = useState(true);
  const [step, setStep] = useState(0);

  function finish() {
    setOpen(false);
    router.post(
      "/onboarding/complete",
      {},
      { preserveScroll: true, preserveState: true },
    );
  }

  // The completion POST redirects back to the dashboard, so a CTA can't both
  // navigate and complete at once -- the redirect would cancel the navigation.
  // Mark it done first, then go where they asked.
  function finishThen(href: string) {
    setOpen(false);
    router.post(
      "/onboarding/complete",
      {},
      { preserveScroll: true, onFinish: () => router.visit(href) },
    );
  }

  const canOrganize = program.organizer_applications_open;

  // The setting is stored bare ("boba-bash"), but it reads as a Slack channel
  // only with the hash.
  const rawChannel = program.support_channel_name ?? "boba-bash";
  const channel = rawChannel.startsWith("#") ? rawChannel : `#${rawChannel}`;

  const steps = [
    {
      title: "welcome.txt",
      heading: `Hi ${name}!`,
      body: (
        <>
          <p>
            A {program.name} is a short, low-pressure hangout: a few hours,
            somewhere comfortable, where teenagers build something small on a
            laptop and drink boba.
          </p>
          <p>
            It's <strong>not a hackathon</strong> - no judges, no prizes, nobody
            stays up all night. Dozens happen around the world in the same week,
            each one run by a teenager in their own city.
          </p>
        </>
      ),
    },
    {
      title: "being-the-poc.txt",
      heading: "You'd be the point of contact",
      body: (
        <>
          <p>
            Apply for your city and tell us roughly what you're planning. It
            takes about five minutes, and{" "}
            <strong>you don't need a venue or a date yet</strong>.
          </p>
          <p>
            Usually a few people apply for the same city, so we pick one point
            of contact per place. It isn't a competition - we're just looking
            for someone reachable who wants to make it happen.
          </p>
        </>
      ),
    },
    {
      title: "after-you-apply.txt",
      heading: "Once you're picked",
      body: (
        <>
          <p>
            You get an event page people can RSVP to, a checklist so nothing
            gets forgotten, and a handbook covering venue, budget and boba. You
            can add your friends to your team and check people in from your
            phone on the day.
          </p>
          <p>
            Stuck at any point? Ask in {channel} - someone from HQ will help.
          </p>
        </>
      ),
    },
  ];

  const current = steps[step];
  const isLast = step === steps.length - 1;

  return (
    <Modal open={open} title={current.title} onClose={finish}>
      <div className="flex flex-col gap-5">
        <h2 className="font-display text-heading text-ink font-bold">
          {current.heading}
        </h2>

        <div className="text-body text-ink-muted flex flex-col gap-3">
          {current.body}
        </div>

        {/* Most people signing in are here to organize -- attendees often have
            no Hack Club account at all. So the attendee path is a footnote
            rather than a co-equal CTA. */}
        {isLast && program.events_enabled && (
          <p className="text-caption text-ink-soft">
            Just here to attend one? Browse{" "}
            <button
              type="button"
              onClick={() => finishThen("/events")}
              className="text-link cursor-pointer underline"
            >
              upcoming events
            </button>{" "}
            instead.
          </p>
        )}

        {/* Step dots double as the progress indicator and as a hint that this is
            short -- three dots reads as "nearly over" in a way "Next" doesn't. */}
        {/* Three CTAs don't fit beside the counter, so the final step puts them
            on their own row rather than letting them wrap raggedly. */}
        <div
          className={`border-line/20 flex flex-col gap-3 border-t pt-4 ${
            isLast ? "" : "sm:flex-row sm:items-center"
          }`}
        >
          <div className="flex flex-1 items-center gap-1.5">
            {steps.map((s, i) => (
              <span
                key={s.title}
                aria-hidden
                className={`h-2 w-2 rounded-full border ${
                  i === step ? "border-line bg-goldenrod" : "border-line/40"
                }`}
              />
            ))}
            <span className="text-caption text-ink-soft ml-2 shrink-0 whitespace-nowrap">
              {step + 1} of {steps.length}
            </span>
          </div>

          {isLast ? (
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={finish}>
                Maybe later
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => finishThen("/guides/organizer-handbook")}
              >
                Read the handbook
              </Button>
              {canOrganize && (
                <Button size="sm" onClick={() => finishThen("/organize")}>
                  Apply to organize
                </Button>
              )}
            </div>
          ) : (
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={finish}>
                Skip
              </Button>
              <Button size="sm" onClick={() => setStep(step + 1)}>
                Next
              </Button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
